import React from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
// ISP-9999 (Kelly, 2026-08-27): hand-authored line-art house via HouseLineArt,
// matching the mobile app's gallery tab icon (assessed from Kelly's IMG_7998) —
// same PrimaryPurple stroke convention as CaptureFrame. MUI's HomeOutlined
// is a filled path rather than an actual stroke, so it didn't match.
import HouseLineArt from "../../../assets/icons/HouseLineArt";
import { colors } from "../../../utils";
import { getLoggedInUser } from "../../../utils/helpers";
import { HEADER_ICON_SIZE, IconWrapper } from "./styles";
import { NavEntryMenuItem } from "./NavEntryMenuItem";

/**
 * Home (Gallery) header nav entry.
 *
 * Gordon 2026-08-18: the Gallery is the user's HOME BASE, so it gets a
 * persistent Home/House icon in the top nav — visible on Discover and every
 * other surface that renders the shared GalleryHeader. This makes the header's
 * nav set self-consistent across pages (Home · Capture · Messages · Discover)
 * instead of the logo being the only way "home".
 *
 * Line-art icon per the icon law (line-art = product voice, no emoji) —
 * the hand-authored assets/icons/HouseLineArt.jsx, same purple/stroke as
 * CaptureFrame. The bundled segmentEditor/Home.svg is a FILLED solid house,
 * so it is deliberately NOT used here.
 *
 * Same Tooltip + IconWrapper + a11y contract (role/tabIndex/aria-label +
 * Enter/Space activation) as the neighbouring header icons, and the same
 * icon/menu dual-variant so it renders identically above and below the phone
 * cut (HeaderOverflowMenu). Not flag-gated — the gallery is live everywhere.
 */
const HomeNavEntry = ({ variant = "icon", ...rest }) => {
  const nav = useNavigate();

  const go = () => {
    const user = getLoggedInUser();
    nav(user?.id ? `/gallery/${user.id}` : "/gallery");
  };

  if (variant === "menu") {
    return (
      <NavEntryMenuItem
        label="Gallery"
        ariaLabel="Gallery home"
        onActivate={go}
        icon={<HouseLineArt color={colors.PrimaryPurple} width={22} height={22} />}
        {...rest}
      />
    );
  }

  return (
    <Tooltip title="Gallery home">
      <IconWrapper
        className="home-button"
        role="button"
        tabIndex={0}
        aria-label="Gallery home"
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            go();
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <HouseLineArt
          color={colors.PrimaryPurple}
          width={HEADER_ICON_SIZE}
          height={HEADER_ICON_SIZE}
        />
      </IconWrapper>
    </Tooltip>
  );
};

export default HomeNavEntry;
